/* Board E · „Lista z odhaczeniem" (`docs/design/ai-buddy-2026-10-09-proposed-exercise/`): the grouped
 * card of exercises outside the catalogue, its rows, the open row's proposal, and board D's failed
 * quick add. Transcribed from the board's CSS onto tokens, each ink as the board draws it: its
 * tertiary stays tertiary, on words too (owner's rule: a KANON colour is never swapped here). */

.group {
  overflow: hidden;
}

.head {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
  padding: var(--space-4) var(--space-18px) var(--space-14px);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3px);
  min-width: 0;
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-detail-name);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
}

.title:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.small {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-3);
}

.counter {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* „1 z 3" on one baseline, with a real gap between the count and „z 3". */
.count {
  display: inline-flex;
  gap: var(--space-1);
  align-items: baseline;
  white-space: nowrap;
}

.segments {
  display: flex;
  gap: var(--space-1);
}

.segment {
  display: block;
  width: 22px;
  height: 4px;
  background: var(--border-2);
  border-radius: var(--radius-hair);
  transition-duration: var(--dur-buddy-segment);
  transition-property: background-color;
  transition-timing-function: var(--ease);
}

.segmentOn {
  background: var(--text);
}

.rows {
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.item {
  border-top: var(--border-hairline) solid var(--border);
}

/* The change of one of Buddy's own picks stands alone in its card, with no line over its row. */
.single .item {
  border-top: none;
}

.row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto 16px;
  gap: var(--space-3);
  align-items: center;
  width: 100%;
  min-height: 64px;
  margin: var(--space-0);
  padding: var(--space-10px) var(--space-18px);
  font-family: var(--font-body);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: none;
  transition-duration: var(--dur-base);
  transition-property: background-color;
  transition-timing-function: var(--ease);
}

.row:hover {
  background: var(--bg-chip);
}

.row:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-minus-2px);
}

.ring {
  width: 22px;
  height: 22px;
  margin: var(--space-3px);
  border-radius: var(--r-circle);
  box-shadow: var(--shadow-buddy-ring);
}

.rowOpen .ring {
  box-shadow: var(--shadow-buddy-ring-open);
}

.check {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  color: var(--bg);
  background: var(--text);
  border-radius: var(--r-circle);
}

.checkGlyph {
  width: 14px;
  height: 14px;
}

.checkGlyph path {
  stroke-dasharray: 16;
  stroke-dashoffset: 0;
}

.skipped {
  color: var(--text-3);
  background: transparent;
  box-shadow: var(--shadow-buddy-ring);
}

.name {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

/* The written name in full, wrapped rather than cut: the trainer reads exactly what will be added. */
.nameTitle {
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  overflow-wrap: anywhere;
}

.nameSub {
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trail {
  max-width: 220px;
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trailChange {
  text-decoration: underline;
  text-decoration-color: var(--border-2);
  text-underline-offset: var(--space-3px);
}

.row:hover .trailChange {
  color: var(--text);
}

.chevron {
  width: 14px;
  height: 14px;
  color: var(--text-3);
  transition-duration: var(--dur-buddy-chevron);
  transition-property: transform, opacity;
  transition-timing-function: var(--ease-buddy-spring);
}

.chevronOn {
  transform: rotate(90deg);
}

.resolvedRow .chevron {
  opacity: 0;
}

/* A row the trainer resolved here: the check pops and draws itself, the row flashes once and its
 * words rise into place (board E, the check from direction A). A row already resolved when the
 * conversation opened stands still (`.quiet`). A row resolved by „Dodaj wszystkie szybko" waits
 * its turn (`--batch-step`). */
.resolving .check {
  animation-name: pop;
  animation-duration: var(--dur-buddy-pop);
  animation-timing-function: var(--ease-buddy-spring);
  animation-delay: calc(var(--batch-step, 0) * var(--dur-buddy-batch-step));
  animation-fill-mode: both;
}

.resolving .checkGlyph path {
  stroke-dashoffset: 16;
  animation-name: draw;
  animation-duration: var(--dur-buddy-draw);
  animation-timing-function: var(--ease);
  animation-delay: calc(var(--dur-buddy-draw-delay) + var(--batch-step, 0) * var(--dur-buddy-batch-step));
  animation-fill-mode: forwards;
}

.resolving .skipped .checkGlyph path {
  stroke-dasharray: none;
  stroke-dashoffset: 0;
  animation-name: none;
}

.resolving .row {
  animation-name: flash;
  animation-duration: var(--dur-buddy-flash);
  animation-timing-function: var(--ease);
  animation-delay: calc(var(--batch-step, 0) * var(--dur-buddy-batch-step));
}

.resolving .name,
.resolving .trail {
  animation-name: settle;
  animation-duration: var(--dur-buddy-settle);
  animation-timing-function: var(--ease-buddy-spring);
  animation-delay: calc(var(--dur-buddy-settle-delay) + var(--batch-step, 0) * var(--dur-buddy-batch-step));
  animation-fill-mode: both;
}

/* The open row's panel folds by its grid row, the board's own technique. */
.fold {
  display: grid;
  grid-template-rows: 1fr;
  transition-duration: var(--dur-buddy-fold);
  transition-property: grid-template-rows;
  transition-timing-function: var(--ease-buddy-spring);
}

.foldInner {
  min-height: 0;
  overflow: hidden;
  opacity: 1;
  transition-duration: var(--dur-slow);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.shut {
  grid-template-rows: 0fr;
}

/* The failed quick add's sentence in the open row: shut, it takes back the gap the panel gives it,
 * so the row stands exactly as it would without it. */
.errorFold {
  transition-property: grid-template-rows, margin-bottom;
}

.errorFold.shut {
  margin-bottom: var(--space-minus-14px);
}

.shut > .foldInner {
  opacity: 0;
}

.panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
  padding: var(--space-1) var(--space-18px) var(--space-18px) var(--space-buddy-row-indent);
}

.kicker {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-3);
}

.facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-1px);
  margin: var(--space-0);
  overflow: hidden;
  background: var(--border);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-buddy-inset);
}

/* The figure under its label, as the board draws it; the label stays first in the reading order. */
.fact {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  padding: var(--space-10px) var(--space-3);
  background: var(--bg-sunken);
}

.fact dt {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.fact dd {
  margin: var(--space-0);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-extrabold);
  overflow-wrap: anywhere;
}

.noProposal {
  margin: var(--space-0);
  padding: var(--space-3) var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-10px);
}

.actionsOne {
  grid-template-columns: minmax(0, 1fr);
}

/* The board's `.btn.lg`: the two adds stand taller than the conversation's other buttons. */
.large {
  justify-content: center;
  height: 42px;
  font-size: var(--fs-dialog-field);
  border-radius: var(--radius-12px);
}

.busyGlyph {
  width: 13px;
  height: 13px;
  border-width: var(--border-hairline);
  border-radius: var(--r-circle);
}

.similarHead {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* Only the way into the catalogue, where no list is offered: it stands where the list's heading would. */
.similarHeadLinkOnly {
  justify-content: flex-start;
}

.link {
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  text-decoration: underline;
  text-decoration-color: var(--border-2);
  text-underline-offset: var(--space-3px);
  cursor: pointer;
  background: none;
  border: none;
}

.link:hover {
  color: var(--text);
}

.link:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.similar {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  overflow: hidden;
  list-style: none;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.similar li + li {
  border-top: var(--border-hairline) solid var(--border);
}

.choice {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: var(--space-0) var(--space-14px);
  font-family: var(--font-body);
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: none;
  transition-duration: var(--dur-base);
  transition-property: background-color;
  transition-timing-function: var(--ease);
}

.choice:hover {
  background: var(--bg-chip);
}

.choice:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-minus-2px);
}

.choiceText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
  padding-block: var(--space-2);
}

.choiceName {
  overflow-wrap: anywhere;
}

/* How a near exercise differs from the written name: a quiet second line, the row's sub-line ink. */
.choiceNote {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  overflow-wrap: anywhere;
}

.choiceGo {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  opacity: 0;
  transition-duration: var(--dur-base);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.choice:hover .choiceGo,
.choice:focus-visible .choiceGo {
  opacity: 1;
}

.choiceGlyph {
  width: 13px;
  height: 13px;
  color: var(--text-3);
}

.searchNote {
  margin: var(--space-0);
  padding: var(--space-3) var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.foot {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
}

.footActs {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.skip {
  padding: var(--space-0) var(--space-1);
}

/* Board D: a quick add that failed, inside the row it failed in. */
.error {
  display: flex;
  gap: var(--space-10px);
  align-items: flex-start;
  margin: var(--space-0);
  padding: var(--space-3) var(--space-14px);
  font-size: var(--fs-note);
  line-height: var(--lh-lede);
  color: var(--text-2);
  background: var(--bg);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-md);
}

.error b {
  color: var(--text);
}

.errorGlyph {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: var(--space-1px);
  color: var(--danger);
}

.groupFoot {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: var(--space-3) var(--space-18px);
  background: var(--bg-sunken);
  border-top: var(--border-hairline) solid var(--border);
}

.opening {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* „Dodaj wszystkie szybko": each name with its proposal, and those it leaves. */
.batchList {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.batchItem {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  padding: var(--space-11px) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.batchName {
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  overflow-wrap: anywhere;
}

.batchLine {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

@keyframes pop {
  0% {
    opacity: 0;
    transform: scale(0.55);
  }

  55% {
    opacity: 1;
    transform: scale(1.08);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes draw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes flash {
  from {
    background: var(--bg-chip);
  }

  to {
    background: transparent;
  }
}

@keyframes settle {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}
